const BASE_URL = import.meta.env.VITE_API_URL ?? '/api'

/**
 * Error thrown for every non-2xx response. `message` mirrors the server's
 * `{ message }` so existing `err.message` callers keep working; `status` and
 * `body` let callers branch on 403 PERMISSION_DENIED, 404, 409 etc. without
 * parsing the message text.
 */
export class ApiError extends Error {
  /**
   * @param {string} message
   * @param {{ status?: number, body?: any, url?: string }} [details]
   */
  constructor(message, { status, body, url } = {}) {
    super(message)
    this.name = 'ApiError'
    /** @type {number|undefined} HTTP status code */
    this.status = status
    /** @type {any} Parsed JSON body (or `{}` when the body was not JSON) */
    this.body = body
    /** @type {string|undefined} Request path relative to the API base */
    this.url = url
  }
}

/**
 * Per-request options. Any other `RequestInit` key (`signal`, `cache`, …) is
 * forwarded to `fetch` untouched.
 * @typedef {Omit<RequestInit, 'headers'> & { headers?: Record<string, string> }} ApiRequestOptions
 */

/** Parsed JSON body of a failed request / upload — the fields the client branches on. */
/** @typedef {{ message?: string, error?: string, code?: string, [key: string]: unknown }} ErrorBody */

// CSRF token cache — fetched once, reused for all mutations
/** @type {string | null} */
let csrfToken = null

async function fetchCsrfToken() {
  try {
    const res = await fetch(`${BASE_URL}/csrf-token`, { credentials: 'include' })
    if (res.ok) {
      const data = await res.json()
      csrfToken = data.token || data.csrfToken || null
    }
  } catch { /* silent — server may not require CSRF */ }
}

// Prevent concurrent refresh attempts
/** @type {Promise<boolean> | null} */
let refreshPromise = null

/** @returns {Promise<boolean>} */
async function tryRefresh() {
  if (refreshPromise) return refreshPromise
  refreshPromise = fetch(`${BASE_URL}/sessions/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' } })
    .then((r) => r.ok)
    .catch(() => false)
    .finally(() => { refreshPromise = null })
  return refreshPromise
}

/**
 * @param {string} path API-relative path ('/users')
 * @param {ApiRequestOptions} [options]
 * @param {boolean} [_retry] internal — false on the single replay
 * @returns {Promise<any>} parsed JSON body — the one untyped boundary; callers annotate the shape at the call site
 */
async function request(path, options = {}, _retry = true) {
  // Auto-fetch CSRF token before first mutation
  if (!csrfToken && options.method && options.method !== 'GET') {
    await fetchCsrfToken()
  }

  /** @type {Record<string, string>} */
  const headers = { 'Content-Type': 'application/json', ...options.headers }
  // Include CSRF token on all mutations
  if (csrfToken && options.method && options.method !== 'GET') {
    headers['X-CSRF-Token'] = csrfToken
  }

  // Spread options first, then override headers and credentials to ensure
  // cookies are always sent and merged headers (with CSRF) are always used.
  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  })

  // On CSRF error, refetch token and retry once
  if (res.status === 403 && _retry) {
    // Read body once — reuse for both CSRF detection and error throw
    /** @type {ErrorBody} */
    const body = await res.json().catch(() => ({}))
    if (body.error === 'CSRF_VALIDATION_FAILED' || body.message?.includes('CSRF')) {
      csrfToken = null
      await fetchCsrfToken()
      return request(path, options, false)
    }
    // Non-CSRF 403 — throw with the already-parsed body (avoid double res.json())
    throw new ApiError(body.message ?? 'Forbidden', { status: 403, body, url: path })
  }

  // On 401, attempt a single token refresh then replay
  if (res.status === 401 && _retry && path !== '/sessions/refresh') {
    const refreshed = await tryRefresh()
    if (refreshed) {
      return request(path, options, false)
    }
    // Session is irrecoverably lost — redirect to auth page to prevent
    // blank-page crashes from components rendering without a user (#31871).
    // Only redirect if we're on a protected /app route to avoid disrupting
    // public pages or the auth page itself.
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/app')) {
      window.location.href = '/auth'
      // Return a never-resolving promise so callers don't continue executing
      // while the redirect is in progress.
      return new Promise(() => {})
    }
    /** @type {ErrorBody} */
    const err = await res.json().catch(() => ({ message: 'Unauthorized' }))
    throw new ApiError(err.message ?? 'Unauthorized', { status: 401, body: err, url: path })
  }

  if (!res.ok) {
    /** @type {ErrorBody} */
    const err = await res.json().catch(() => ({ message: res.statusText }))
    throw new ApiError(err.message ?? 'API error', { status: res.status, body: err, url: path })
  }
  return res.json()
}

/**
 * The app-wide client: cookie session, CSRF header on mutations, one refresh +
 * replay on 401. Every method resolves with the parsed JSON body.
 * @typedef {object} ApiClient
 * @property {(path: string) => Promise<any>} get
 * @property {(path: string, body?: unknown) => Promise<any>} post
 * @property {(path: string, body?: unknown) => Promise<any>} put
 * @property {(path: string, body?: unknown) => Promise<any>} patch
 * @property {(path: string) => Promise<any>} delete
 * @property {typeof upload} upload
 */

/** @type {ApiClient} */
export const api = {
  get: (path) => request(path),
  post: (path, body) =>
    request(path, { method: 'POST', body: JSON.stringify(body) }),
  put: (path, body) =>
    request(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: (path, body) =>
    request(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: (path) => request(path, { method: 'DELETE' }),
  upload,
}

/**
 * Multipart upload with progress (fetch cannot report upload progress, so this uses XMLHttpRequest).
 * Same cookie credentials and CSRF header as `request`; retries once on a CSRF failure.
 * @param {string} path                              API path, e.g. '/storage/upload'
 * @param {FormData} formData
 * @param {{ onProgress?: (fraction: number) => void, signal?: AbortSignal }} [opts]
 * @param {boolean} [_retry] internal — false on the single CSRF replay
 * @returns {Promise<any>} parsed JSON body
 */
async function upload(path, formData, { onProgress, signal } = {}, _retry = true) {
  if (!csrfToken) await fetchCsrfToken()
  const result = await /** @type {Promise<{ status: number, body: ErrorBody }>} */ (new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${BASE_URL}${path}`)
    xhr.withCredentials = true
    if (csrfToken) xhr.setRequestHeader('X-CSRF-Token', csrfToken)
    if (xhr.upload && onProgress) {
      xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded / e.total) }
    }
    if (signal) {
      if (signal.aborted) return reject(new DOMException('Aborted', 'AbortError'))
      signal.addEventListener('abort', () => xhr.abort(), { once: true })
    }
    xhr.onerror = () => reject(new Error('Network error during upload'))
    xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'))
    xhr.onload = () => {
      /** @type {ErrorBody} */
      let body = {}
      try { body = xhr.responseText ? JSON.parse(xhr.responseText) : {} } catch { body = { message: xhr.statusText } }
      resolve({ status: xhr.status, body })
    }
    xhr.send(formData)
  }))
  if (result.status === 403 && _retry && (result.body.error === 'CSRF_VALIDATION_FAILED' || result.body.message?.includes('CSRF'))) {
    csrfToken = null
    await fetchCsrfToken()
    return upload(path, formData, { onProgress, signal }, false)
  }
  if (result.status < 200 || result.status >= 300) {
    // Same error type as `request` so callers branch on `err.status` / `err.body.code` uniformly.
    throw new ApiError(result.body.message ?? 'Upload failed', { status: result.status, body: result.body, url: path })
  }
  return result.body
}
